import { useState, useEffect, useRef } from 'react';
import { getNewlyUnlockedItems } from './guideCatalogue';

export function XpFlashBubble({ amount }: { amount: number }) {
  const [displayed, setDisplayed] = useState(0);
  useEffect(() => {
    if (amount <= 0) return;
    const duration = 600;
    const start = performance.now();
    let raf: number;
    function tick(now: number) {
      const progress = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayed(Math.round(eased * amount));
      if (progress < 1) raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [amount]);
  return <div className="xp-flash">+{displayed} XP ✨</div>;
}

export function ConfettiCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    const pieces: { x: number; y: number; vx: number; vy: number; color: string; size: number; angle: number; spin: number }[] = [];
    const colors = ['var(--color-accent)', 'var(--color-secondary)', 'var(--color-warning)', 'var(--color-success)', 'var(--color-info)', 'var(--color-warning)', 'var(--color-accent)'];
    for (let i = 0; i < 80; i++) {
      pieces.push({
        x: Math.random() * canvas.width,
        y: -10,
        vx: (Math.random() - 0.5) * 6,
        vy: Math.random() * 4 + 2,
        color: colors[Math.floor(Math.random() * colors.length)],
        size: Math.random() * 8 + 4,
        angle: Math.random() * Math.PI * 2,
        spin: (Math.random() - 0.5) * 0.2,
      });
    }
    let animId: number;
    let alive = true;
    function draw() {
      if (!ctx || !canvas || !alive) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      pieces.forEach(p => {
        p.x += p.vx; p.y += p.vy; p.angle += p.spin; p.vy += 0.08;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.angle);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = Math.max(0, 1 - p.y / canvas.height);
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.5);
        ctx.restore();
      });
      animId = requestAnimationFrame(draw);
    }
    draw();
    return () => { alive = false; cancelAnimationFrame(animId); };
  }, []);
  return <canvas ref={canvasRef} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', borderRadius: 'inherit' }} />;
}

export function XpLevelUpCelebration({ level, name, onDismiss }: { level: number; name: string; onDismiss: () => void }) {
  const newItems = getNewlyUnlockedItems(level);
  useEffect(() => {
    const t = setTimeout(onDismiss, 5000);
    return () => clearTimeout(t);
  }, [onDismiss]);
  return (
    <div
      onClick={onDismiss}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.72)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer',
        animation: 'lvlUpFadeIn 0.35s ease-out',
        overflow: 'hidden',
      }}
    >
      <ConfettiCanvas />
      <div
        style={{
          position: 'relative', zIndex: 1,
          background: 'var(--color-bg-primary)',
          border: '2px solid var(--color-accent-light)',
          borderRadius: 'var(--radius-xl)',
          padding: '36px 40px',
          textAlign: 'center',
          maxWidth: 340,
          width: '88vw',
          boxShadow: '0 20px 60px var(--color-accent-light)',
          animation: 'lvlUpBadgePop 0.5s cubic-bezier(0.34,1.56,0.64,1) 0.1s both',
        }}
      >
        <div style={{ fontSize: 'var(--fs-8xl)', marginBottom: 8, animation: 'lvlUpSpin 0.6s ease-out 0.15s both' }}>🧵</div>
        <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 700, letterSpacing: 2, color: 'var(--color-accent)', textTransform: 'uppercase', marginBottom: 6 }}>Level Up!</div>
        <div style={{ fontSize: 'var(--fs-3xl-plus)', fontWeight: 900, color: '#fff', marginBottom: 4, lineHeight: 1.1 }}>Level {level}</div>
        <div style={{ fontSize: 'var(--fs-lg)', fontWeight: 700, color: 'var(--color-accent)', marginBottom: 10 }}>{name}</div>
        {newItems.length > 0 && (
          <div style={{
            background: 'var(--color-bg-surface)', borderRadius: 'var(--radius-md)', padding: '10px 14px',
            marginBottom: 14, border: '1px solid var(--color-border)',
          }}>
            <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: 'var(--color-accent)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
              You unlocked:
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center' }}>
              {newItems.slice(0, 8).map((item, i) => (
                <span key={i} style={{
                  fontSize: 'var(--fs-xs)', color: 'var(--color-accent)', background: 'color-mix(in srgb, var(--color-accent) 20%, transparent)',
                  padding: '3px 8px', borderRadius: 'var(--radius-sm)', fontWeight: 600,
                }}>
                  {item}
                </span>
              ))}
              {newItems.length > 8 && (
                <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-accent)', fontWeight: 600 }}>
                  +{newItems.length - 8} more
                </span>
              )}
            </div>
          </div>
        )}
        <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-accent)' }}>Tap anywhere to continue</div>
      </div>
    </div>
  );
}

export const LazyFallback = () => (
  <div style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--gradient-page)' }}>
    <div style={{ width: 40, height: 40, border: '3px solid var(--color-border)', borderTopColor: 'var(--color-accent)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
  </div>
);
